import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { isApiConfigured, postSignin } from "../../apiClient";
import { buildAuthHrefWithNext, navigateAfterSignIn } from "../../navigateAfterAuth";
import SiteLogo from "../../components/SiteLogo";
import PageSeo from "../../components/PageSeo";
import old1_PublicSiteFooter from "../components/old1_PublicSiteFooter";

function old1_SigninPage({ onSignIn, currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  const signupHref = useMemo(
    () => buildAuthHrefWithNext("/old1/signup", nextParam),
    [nextParam]
  );
  const showWelcome = Boolean(location.state?.welcome);
  const signupData = location.state?.signupData || null;
  const passwordResetHint = Boolean(location.state?.passwordReset);
  const resetEmailHint = typeof location.state?.emailHint === "string" ? location.state.emailHint : "";
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const rawEmail = form.elements.signinEmail.value || "";
    const normalizedEmail = rawEmail.trim().toLowerCase();
    const rawPassword = form.elements.signinPassword.value || "";
    setSubmitError("");

    if (isApiConfigured()) {
      setSubmitting(true);
      try {
        const { user, token } = await postSignin({
          email: normalizedEmail || rawEmail.trim(),
          password: rawPassword,
          signupType: signupData?.signupType,
        });
        onSignIn(user, { token });
        navigateAfterSignIn(navigate, user, { nextPath: nextParam });
        return;
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : "Sign in failed");
        setSubmitting(false);
        return;
      }
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const role = signupData?.signupType || currentUser?.role || "applicant";
    const nextUser = {
      name: "CJ Jayamaha",
      email: signupData?.email || rawEmail,
      role,
      ...(role === "applicant"
        ? { profileActive: false, profileComplete: false }
        : {}),
    };

    onSignIn(nextUser);

    navigateAfterSignIn(navigate, nextUser, { nextPath: nextParam });
  };

  const legacyAside = (
    <aside className="jw-auth-aside" aria-label="Why sign in to JOBUWIN">
      <span className="jw-public-chip">
        <ShieldCheck className="jw-public-icon" aria-hidden="true" />
        Secure access
      </span>
      <h1>Welcome back to genuine hiring.</h1>
      <p>
        Sign in to continue meaningful conversations, manage genuine opportunities, and connect through a
        platform designed to reduce noise, spam, and fake interactions.
      </p>
      <div className="jw-auth-benefits">
        <span>
          <CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Continue conversations with genuine
          applicants and hiring managers
        </span>
        <span>
          <CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Manage verified opportunities and
          trusted activity
        </span>
        <span>
          <CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Built to reduce fake accounts and AI
          generated noise
        </span>
      </div>
    </aside>
  );

  const legacyFormCard = (
    <section className="signup-card jw-public-card jw-auth-card">
      <Link className="back-link jw-public-back-link" to="/old1/home">
        <ArrowLeft className="jw-public-icon" aria-hidden="true" />
        Back to home
      </Link>
      <p className="eyebrow jw-public-eyebrow">Sign in</p>
      <h2 className={showWelcome ? undefined : "signin-heading-lede"}>
        {showWelcome ? "Welcome to JOBUWIN.com" : "Hello, let's sign in"}
      </h2>
      {showWelcome ? (
        <p className="subtitle">Your account is verified. Sign in to continue.</p>
      ) : (
        <p className="subtitle">Use your email and password to access your JOBUWIN workspace.</p>
      )}

      <form className="signup-form jw-auth-form" action="#" method="post" onSubmit={handleSubmit}>
        {passwordResetHint ? (
          <p className="subtitle signin-password-reset-intro jw-auth-note">
            Use your email below, then choose &ldquo;Forgot password?&rdquo; to start a reset. You can sign back in
            after updating your password.
          </p>
        ) : null}
        <label htmlFor="signinEmailLegacy">Email address</label>
        <div className="jw-auth-field">
          <input
            id="signinEmailLegacy"
            name="signinEmail"
            type="email"
            placeholder="Enter your email"
            defaultValue={signupData?.email || resetEmailHint || ""}
            required
          />
        </div>

        <label htmlFor="signinPasswordLegacy">Password</label>
        <div className="jw-auth-field">
          <input
            id="signinPasswordLegacy"
            name="signinPassword"
            type="password"
            placeholder="Enter your password"
            required
          />
        </div>
        <Link className="forgot-password-link" to="/old1/forgot-password">
          Forgot password?
        </Link>

        {submitError ? (
          <p className="form-inline-alert-danger" role="alert">
            {submitError}
          </p>
        ) : null}

        <button
          type="submit"
          className="submit-button jw-public-button jw-public-button--primary"
          disabled={submitting}
        >
          {submitting ? "Signing in..." : "Sign in"}
          <ArrowRight className="jw-public-icon" aria-hidden="true" />
        </button>
      </form>

      <p className="jw-auth-switch">
        New to Jobuwin? <Link to={signupHref}>Create an account</Link>
      </p>
    </section>
  );

  return (
    <div className="signup-page jw-public-page jw-auth-page">
      <PageSeo
        title="Sign in"
        description="Sign in to JOBUWIN to continue genuine hiring conversations, manage opportunities, and connect with real people."
        path="/old1/signin"
        noindex
      />
      <nav className="landing-nav jw-public-nav" aria-label="Authentication navigation">
        <div className="landing-nav-shell jw-public-nav__shell">
          <SiteLogo />
          <div className="nav-buttons landing-nav-actions">
            <button
              type="button"
              className="jw-public-button jw-public-button--text nav-button-current landing-nav-login"
              aria-current="page"
            >
              Sign in
            </button>
            <button
              type="button"
              className="jw-public-button jw-public-button--primary landing-nav-cta"
              onClick={() => navigate(signupHref)}
            >
              Sign up
              <ArrowRight className="jw-public-icon" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>

      <main id="main-content" className="signup-container jw-auth-shell">
        {legacyAside}
        {legacyFormCard}
      </main>

      <old1_PublicSiteFooter />
    </div>
  );
}

export default old1_SigninPage;
